// Waveforms for the timeline and the pads: a double-sided (min/max) overview
// of an object, drawn as one filled shape.
//
// Reading a whole decoded object for every zoom step and every clip window is
// what made the old overview stride past samples to stay cheap — and a strided
// read misses transients. Instead each object is summarised ONCE into min/max
// per `SUMMARY_BIN` frames, and a window is then read from the summary: a
// 3-minute stereo render is ~17M samples but ~34k bins. Only a window zoomed
// in past one bin per bucket reads raw samples, and that window is small by
// construction.

/** Frames per summary bin. */
export const SUMMARY_BIN = 256;

/** An object's min/max per `binFrames` frames, channels folded together. */
export interface WaveSummary {
  sampleRate: number;
  /** Frames in the object. */
  length: number;
  binFrames: number;
  min: Float32Array;
  max: Float32Array;
  /** The object's loudest sample, absolute — what a view normalises to. */
  peak: number;
}

/** The planar channels of a decoded object — an AudioBuffer's shape, minus the context. */
export interface Channels {
  sampleRate: number;
  length: number;
  channels: readonly Float32Array[];
}

/** One pass over every sample: min/max per bin, across channels. */
export function summarize(src: Channels, binFrames = SUMMARY_BIN): WaveSummary {
  const bins = Math.max(1, Math.ceil(src.length / binFrames));
  const min = new Float32Array(bins);
  const max = new Float32Array(bins);
  let peak = 0;
  for (let b = 0; b < bins; b++) {
    const from = b * binFrames;
    const to = Math.min(src.length, from + binFrames);
    let lo = 0;
    let hi = 0;
    for (const data of src.channels) {
      for (let i = from; i < to; i++) {
        const v = data[i]!;
        if (v < lo) lo = v;
        else if (v > hi) hi = v;
      }
    }
    min[b] = lo;
    max[b] = hi;
    peak = Math.max(peak, hi, -lo);
  }
  return { sampleRate: src.sampleRate, length: src.length, binFrames, min, max, peak };
}

/**
 * `buckets` min/max pairs over `[offsetSec, offsetSec + durationSec]`,
 * interleaved: `[min0, max0, min1, max1, …]`. From raw samples when they are
 * at hand and the window is finer than a summary bin; from the summary
 * otherwise. Past the object's end reads as silence.
 */
export function envelope(
  source: { summary: WaveSummary; raw?: Channels | null },
  buckets: number,
  offsetSec: number,
  durationSec: number,
): Float32Array {
  const { summary, raw } = source;
  const out = new Float32Array(buckets * 2);
  const rate = summary.sampleRate;
  const first = Math.max(0, offsetSec * rate);
  const perBucket = (durationSec * rate) / buckets;
  if (!(perBucket > 0)) return out;

  if (raw && perBucket < summary.binFrames) {
    const scale = raw.sampleRate / rate;
    for (let b = 0; b < buckets; b++) {
      const from = Math.floor((first + b * perBucket) * scale);
      const to = Math.min(raw.length, Math.max(from + 1, Math.floor((first + (b + 1) * perBucket) * scale)));
      let lo = 0;
      let hi = 0;
      for (const data of raw.channels) {
        for (let i = from; i < to; i++) {
          const v = data[i]!;
          if (v < lo) lo = v;
          else if (v > hi) hi = v;
        }
      }
      out[b * 2] = lo;
      out[b * 2 + 1] = hi;
    }
    return out;
  }

  const bins = summary.min.length;
  const perBin = summary.binFrames;
  for (let b = 0; b < buckets; b++) {
    const from = Math.floor((first + b * perBucket) / perBin);
    // At least one bin, so a bucket narrower than a bin still reads the bin it sits in.
    const to = Math.min(bins, Math.max(from + 1, Math.ceil((first + (b + 1) * perBucket) / perBin)));
    let lo = 0;
    let hi = 0;
    for (let i = from; i < to; i++) {
      if (summary.min[i]! < lo) lo = summary.min[i]!;
      if (summary.max[i]! > hi) hi = summary.max[i]!;
    }
    out[b * 2] = lo;
    out[b * 2 + 1] = hi;
  }
  return out;
}

/** The gain that brings an object's loudest sample to near full height —
 * a quiet pad reads as a shape, not a line. Capped, so near-silence stays
 * near-silent rather than blown up into noise. */
export function normalGain(summary: WaveSummary, headroom = 0.95, maxGain = 8): number {
  return summary.peak > 0 ? Math.min(maxGain, headroom / summary.peak) : 1;
}

/**
 * Draw an interleaved min/max envelope into `[0, width] × [0, height]` as a
 * single filled path — out along the maxima, back along the minima, one
 * point per bucket centre — so thousands of buckets are one fill, not
 * thousands of rects. Every bucket keeps at least a hairline, so silence
 * still reads as a line through the middle.
 */
export function drawEnvelope(
  g: CanvasRenderingContext2D,
  data: Float32Array,
  width: number,
  height: number,
  fill: string,
): void {
  const n = data.length >> 1;
  if (n === 0) return;
  const mid = height / 2;
  const half = height / 2 - 1;
  const step = width / n;
  const hair = 0.5;
  const top = (i: number): number => mid - Math.max(hair, Math.min(1, data[i * 2 + 1]!) * half);
  const bottom = (i: number): number => mid + Math.max(hair, Math.min(1, -data[i * 2]!) * half);
  g.fillStyle = fill;
  g.beginPath();
  g.moveTo(0, top(0));
  for (let i = 0; i < n; i++) g.lineTo((i + 0.5) * step, top(i));
  g.lineTo(width, top(n - 1));
  g.lineTo(width, bottom(n - 1));
  for (let i = n - 1; i >= 0; i--) g.lineTo((i + 0.5) * step, bottom(i));
  g.lineTo(0, bottom(0));
  g.closePath();
  g.fill();
}

/** Size a canvas to `width × height` CSS px at the device's pixel ratio and
 * return its context, cleared and scaled; null when there is none. */
export function prepareCanvas(canvas: HTMLCanvasElement, width: number, height: number): CanvasRenderingContext2D | null {
  const dpr = window.devicePixelRatio || 1;
  const w = Math.max(1, Math.floor(width * dpr));
  const h = Math.max(1, Math.floor(height * dpr));
  if (canvas.width !== w) canvas.width = w;
  if (canvas.height !== h) canvas.height = h;
  const g = canvas.getContext("2d");
  if (!g) return null;
  g.setTransform(dpr, 0, 0, dpr, 0, 0);
  g.clearRect(0, 0, width, height);
  return g;
}
